/**
 * Budget Project Slice
 *
 * Manages budget project state and calculations.
 * No defensive programming - direct field access only.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { HttpClient } from '../../api/service';
import { CalculationFigures } from '../../views/budget/utils/budgetUnitCalculations';
import { extractErrorMessage } from '../../utils/errorHandler';
import { RootState } from '../rootState';
import { buildSavePayloadFromState } from '../../views/budget/actions/budgetSaveActions';

interface BudgetProject {
  id: number;
  name: string;
  client: string;
  therapeutic_area: string;
  study_type: string;
  study_phase: string;
  start_date: string;
  end_date: string;
  status: string;
  total_budget: number;
  budget_used: number;
  budget_positions: any[];
  external_positions: any[];
  phases: any[];
  calculation_figures: CalculationFigures;
  unit_hours_data: Record<string, number>;
  billing_rates_data: any; // Array of position billing rates
}

interface BudgetProjectState {
  budgets: BudgetProject[];
  currentBudget: BudgetProject | null;
  loading: boolean;
  error: string | null;
  calculationPreview: any | null;
}

const initialState: BudgetProjectState = {
  budgets: [],
  currentBudget: null,
  loading: false,
  error: null,
  calculationPreview: null,
};

// Async thunks
export const fetchBudgetProjects = createAsyncThunk(
  'budgetProject/fetchAll',
  async () => {
    const response = await HttpClient.get<{ results: BudgetProject[] }, null>('/api/budget-module/projects/');
    return response.results;
  }
);

export const fetchBudgetDetail = createAsyncThunk(
  'budgetProject/fetchDetail',
  async (budgetId: number) => {
    return await HttpClient.get<BudgetProject, null>(`/api/budget-module/projects/${budgetId}/`);
  }
);

export const updateCalculationFigures = createAsyncThunk(
  'budgetProject/updateCalculationFigures',
  async ({ budgetId, figures }: { budgetId: number; figures: CalculationFigures }) => {
    return await HttpClient.post<BudgetProject, { calculation_figures: CalculationFigures }>(
      `/api/budget-module/projects/${budgetId}/update-calculation-figures/`,
      { calculation_figures: figures }
    );
  }
);

/**
 * Preview recalculation with unsaved changes
 *
 * Sends current unsaved changes to backend for calculation preview.
 * Backend applies changes temporarily without saving to DB.
 */
export const previewRecalculation = createAsyncThunk(
  'budgetProject/previewRecalculation',
  async (budgetId: number, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;

      // DEBUG: Log Redux state
      console.log('[previewRecalculation] Redux changeTracker state:', {
        changes: state.budgetChangeTracker.changes,
        modifiedFields: state.budgetChangeTracker.modifiedFields
      });

      const { payload } = buildSavePayloadFromState(state);

      // DEBUG: Log payload being sent
      console.log('[previewRecalculation] Payload being sent to API:', payload);

      return await HttpClient.post<any, Record<string, any>>(
        `/api/budget-module/projects/${budgetId}/preview-recalculation/`,
        payload
      );
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const applyRecalculation = createAsyncThunk(
  'budgetProject/applyRecalculation',
  async (budgetId: number, { rejectWithValue }) => {
    try {
      return await HttpClient.post<BudgetProject, null>(
        `/api/budget-module/projects/${budgetId}/apply-recalculation/`,
        null
      );
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const updateStudyData = createAsyncThunk(
  'budgetProject/updateStudyData',
  async ({ budgetId, data }: { budgetId: number; data: any }) => {
    return await HttpClient.patch<BudgetProject, any>(
      `/api/budget-module/projects/`,
      budgetId,
      data
    );
  }
);

export const updatePhases = createAsyncThunk(
  'budgetProject/updatePhases',
  async ({ budgetId, phases }: { budgetId: number; phases: any[] }) => {
    return await HttpClient.patch<BudgetProject, { phases: any[] }>(
      `/api/budget-module/projects/`,
      budgetId,
      { phases }
    );
  }
);

export const updateUnitHours = createAsyncThunk(
  'budgetProject/updateUnitHours',
  async ({ budgetId, unitHours }: { budgetId: number; unitHours: Record<string, number> }) => {
    return await HttpClient.patch<BudgetProject, { unit_hours_data: Record<string, number> }>(
      `/api/budget-module/projects/`,
      budgetId,
      { unit_hours_data: unitHours }
    );
  }
);

export const updateBillingRates = createAsyncThunk(
  'budgetProject/updateBillingRates',
  async ({ budgetId, rates }: { budgetId: number; rates: any }) => {
    return await HttpClient.patch<BudgetProject, { billing_rates_data: any }>(
      `/api/budget-module/projects/`,
      budgetId,
      { billing_rates_data: rates }
    );
  }
);

export const updateBudgetPositions = createAsyncThunk(
  'budgetProject/updateBudgetPositions',
  async ({ budgetId, positions }: { budgetId: number; positions: any[] }) => {
    return await HttpClient.patch<BudgetProject, { budget_positions: any[] }>(
      `/api/budget-module/projects/`,
      budgetId,
      { budget_positions: positions }
    );
  }
);

const budgetProjectSlice = createSlice({
  name: 'budgetProject',
  initialState,
  reducers: {
    clearCalculationPreview: (state) => {
      state.calculationPreview = null;
    },
    setCurrentBudget: (state, action: PayloadAction<number>) => {
      const budget = state.budgets.find(b => b.id === action.payload);
      state.currentBudget = budget || null;
    },
    resetCalculationState: (state) => {
      // Reset calculation-related state while preserving budget data
      state.calculationPreview = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch all budgets
    builder
      .addCase(fetchBudgetProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBudgetProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.budgets = action.payload;
      })
      .addCase(fetchBudgetProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch budgets';
      });

    // Fetch budget detail
    builder
      .addCase(fetchBudgetDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBudgetDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBudget = action.payload;
        // Update in list too
        const index = state.budgets.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.budgets[index] = action.payload;
        } else {
          state.budgets.push(action.payload);
        }
      })
      .addCase(fetchBudgetDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch budget detail';
      });

    // Update calculation figures
    builder
      .addCase(updateCalculationFigures.fulfilled, (state, action) => {
        // The backend now returns the full updated budget object
        if (state.currentBudget && state.currentBudget.id === action.payload.id) {
          state.currentBudget = action.payload;
        }
        const index = state.budgets.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.budgets[index] = action.payload;
        }
      });

    // Preview recalculation
    builder
      .addCase(previewRecalculation.pending, (state) => {
        state.calculationPreview = null;
        state.error = null;
      })
      .addCase(previewRecalculation.fulfilled, (state, action) => {
        state.calculationPreview = action.payload;
        state.error = null;
      })
      .addCase(previewRecalculation.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || 'Calculation preview failed';
      });

    // Apply recalculation
    builder
      .addCase(applyRecalculation.pending, (state) => {
        state.error = null;
      })
      .addCase(applyRecalculation.fulfilled, (state, action) => {
        state.calculationPreview = null;
        state.error = null;
        if (state.currentBudget && state.currentBudget.id === action.payload.id) {
          state.currentBudget = action.payload;
        }
        const index = state.budgets.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.budgets[index] = action.payload;
        }
      })
      .addCase(applyRecalculation.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || 'Apply recalculation failed';
      });

    // Update study data
    builder
      .addCase(updateStudyData.fulfilled, (state, action) => {
        if (state.currentBudget && state.currentBudget.id === action.payload.id) {
          state.currentBudget = { ...state.currentBudget, ...action.payload };
        }
        const index = state.budgets.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.budgets[index] = { ...state.budgets[index], ...action.payload };
        }
      });

    // Update phases
    builder
      .addCase(updatePhases.fulfilled, (state, action) => {
        if (state.currentBudget && state.currentBudget.id === action.payload.id) {
          state.currentBudget.phases = action.payload.phases;
        }
        const index = state.budgets.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.budgets[index].phases = action.payload.phases;
        }
      });

    // Update unit hours
    builder
      .addCase(updateUnitHours.fulfilled, (state, action) => {
        if (state.currentBudget && state.currentBudget.id === action.payload.id) {
          state.currentBudget.unit_hours_data = action.payload.unit_hours_data;
        }
        const index = state.budgets.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.budgets[index].unit_hours_data = action.payload.unit_hours_data;
        }
      });

    // Update billing rates
    builder
      .addCase(updateBillingRates.fulfilled, (state, action) => {
        if (state.currentBudget && state.currentBudget.id === action.payload.id) {
          state.currentBudget.billing_rates_data = action.payload.billing_rates_data;
        }
        const index = state.budgets.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.budgets[index].billing_rates_data = action.payload.billing_rates_data;
        }
      });

    // Update budget positions
    builder
      .addCase(updateBudgetPositions.fulfilled, (state, action) => {
        if (state.currentBudget && state.currentBudget.id === action.payload.id) {
          state.currentBudget.budget_positions = action.payload.budget_positions;
        }
        const index = state.budgets.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.budgets[index].budget_positions = action.payload.budget_positions;
        }
      });
  },
});

export const { 
  clearCalculationPreview, 
  setCurrentBudget,
  resetCalculationState 
} = budgetProjectSlice.actions;
export default budgetProjectSlice.reducer;